import { BLANK_LABEL } from "./adhocConditions";
import type { ValueOption } from "./valueOptions";

/** What a values checklist draws for a search, and how many of the rows it shows are ticked. */
export type ChecklistRows = {
  /** The value rows: the options whose label holds the search, then chosen values no option carries any more. */
  listed: ValueOption[];
  /** Whether the (blank) row shows: the checklist offers one and the search matches its label. */
  blankShown: boolean;
  /** How many rows show, (blank) included. */
  shown: number;
  /** How many of the shown rows are ticked, (blank) included. */
  ticked: number;
};

/**
 * The rows a values checklist shows for a search, case-insensitively.
 *
 * A value a stale condition names but the rows no longer carry stays listed whatever the search, so it can be
 * unticked.
 *
 * @param options - Every value the checklist offers.
 * @param chosen - The ticked values, which may name values no option carries.
 * @param search - The term in the "Find a value" box; empty shows every option.
 * @param blank - Whether (blank) is ticked, or `undefined` where the checklist offers no (blank) row.
 */
export function checklistRows(
  options: readonly ValueOption[],
  chosen: ReadonlySet<string>,
  search: string,
  blank: boolean | undefined,
): ChecklistRows {
  const needle = search.toLowerCase();
  const offered = new Set(options.map((option) => option.value));
  const shown = search ? options.filter((option) => option.label.toLowerCase().includes(needle)) : options;
  const stale = [...chosen].filter((value) => !offered.has(value));
  const listed = [...shown, ...stale.map((value) => ({ value, label: value, count: 0 }))];
  const blankShown = blank !== undefined && (!search || BLANK_LABEL.includes(needle));
  const tickedValues = listed.filter((option) => chosen.has(option.value)).length;
  return {
    listed,
    blankShown,
    shown: listed.length + (blankShown ? 1 : 0),
    ticked: tickedValues + (blankShown && blank ? 1 : 0),
  };
}

/** Whether rows show and every one of them is ticked, which turns Select all into unticking them. */
export function allShownTicked(rows: ChecklistRows): boolean {
  return rows.shown > 0 && rows.ticked === rows.shown;
}

/**
 * The ticked values after a click on one value's row.
 *
 * A multi-value checklist ticks or unticks the value alone; a single-value one holds only the value ticked last, and
 * unticking it leaves none.
 */
export function toggleValue(chosen: ReadonlySet<string>, value: string, multiple: boolean): Set<string> {
  if (!multiple) return chosen.has(value) ? new Set() : new Set([value]);
  const next = new Set(chosen);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

/**
 * The ticks after Select all: every shown row ticked, or every shown row unticked when all were ticked already.
 *
 * Values the search hides keep their ticks, and so does (blank) while it is hidden.
 *
 * @param chosen - The ticked values before the click.
 * @param rows - What the checklist shows, from {@link checklistRows}.
 * @param blank - Whether (blank) is ticked before the click; `false` where the checklist offers no (blank) row.
 */
export function selectAllShown(
  chosen: ReadonlySet<string>,
  rows: ChecklistRows,
  blank: boolean,
): { chosen: Set<string>; blank: boolean } {
  const untick = allShownTicked(rows);
  const next = new Set(chosen);
  for (const option of rows.listed) {
    if (untick) next.delete(option.value);
    else next.add(option.value);
  }
  return { chosen: next, blank: rows.blankShown ? !untick : blank };
}
